import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { BASE_END_POINT } from '../../../config';

const initialState = {
  stripeProducts: {},
  stripePublishableKey: null,
  isLoading: false,
  isLoadingPublishableKey: false,
  error: null,
  publishableKeyError: null,
  lastFetched: null
};

/**
 * Fetch Stripe product configuration from backend API.
 * This replaces the env var parsing with API call.
 */
export const fetchStripeProducts = createAsyncThunk(
  'configuration/fetchStripeProducts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${BASE_END_POINT}/api/v1/configuration/public/STRIPE_PRODUCTS`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Expected response format: { status: "Ok", message: "Success", data: { configValue: {...} } }
      if (data && data.data && data.data.configValue) {
        return data.data.configValue;
      }

      console.error('Unexpected API response format:', data);
      throw new Error('Invalid response format from configuration API');
    } catch (error) {
      console.error('Failed to fetch Stripe products configuration:', error);
      return rejectWithValue(
        error.message || 'Failed to load pricing'
      );
    }
  }
);

/**
 * Fetch Stripe publishable key from backend API.
 * This replaces the REACT_APP_STRIPE_PUBLISHABLE_KEY environment variable.
 */
export const fetchStripePublishableKey = createAsyncThunk(
  'configuration/fetchStripePublishableKey',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${BASE_END_POINT}/api/v1/configuration/public/STRIPE_PUBLISHABLE_KEY`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Expected response format: { status: "Ok", message: "Success", data: { configValue: "pk_..." } }
      if (data && data.data && data.data.configValue) {
        return data.data.configValue;
      }

      console.error('Unexpected API response format:', data);
      throw new Error('Invalid response format from configuration API');
    } catch (error) {
      console.error('Failed to fetch Stripe publishable key:', error);
      return rejectWithValue(
        error.message || 'Failed to load Stripe configuration'
      );
    }
  }
);

const configurationSlice = createSlice({
  name: 'configuration',
  initialState,
  reducers: {
    clearConfiguration(state) {
      state.stripeProducts = {};
      state.stripePublishableKey = null;
      state.error = null;
      state.publishableKeyError = null;
      state.lastFetched = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStripeProducts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchStripeProducts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.stripeProducts = action.payload;
        state.lastFetched = new Date().toISOString();
        state.error = null;
      })
      .addCase(fetchStripeProducts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchStripePublishableKey.pending, (state) => {
        state.isLoadingPublishableKey = true;
        state.publishableKeyError = null;
      })
      .addCase(fetchStripePublishableKey.fulfilled, (state, action) => {
        state.isLoadingPublishableKey = false;
        state.stripePublishableKey = action.payload;
        state.publishableKeyError = null;
      })
      .addCase(fetchStripePublishableKey.rejected, (state, action) => {
        state.isLoadingPublishableKey = false;
        state.publishableKeyError = action.payload;
      });
  }
});

// Selectors
export const stripeProductsSelector = (state) =>
  state.configuration.stripeProducts;
export const stripePublishableKeySelector = (state) =>
  state.configuration.stripePublishableKey;
export const isLoadingConfigurationSelector = (state) =>
  state.configuration.isLoading;
export const isLoadingPublishableKeySelector = (state) =>
  state.configuration.isLoadingPublishableKey;
export const configurationErrorSelector = (state) => state.configuration.error;
export const publishableKeyErrorSelector = (state) =>
  state.configuration.publishableKeyError;

// Helper selectors for pricing (accepts PLAN_TYPE constants)
// Usage: const price = useSelector(getStripePriceSelector(PLAN_TYPE.HIRING_PROFILE_STARTER));
export const getStripePriceSelector = (planTypeId) => (state) => {
  const products = state.configuration.stripeProducts;
  return products[planTypeId.toString()]?.price || 0;
};

export const getStripePriceIdSelector = (planTypeId) => (state) => {
  const products = state.configuration.stripeProducts;
  return products[planTypeId.toString()]?.id || '';
};

export const getStripeOriginalPriceSelector = (planTypeId) => (state) => {
  const products = state.configuration.stripeProducts;
  return products[planTypeId.toString()]?.originalPrice || 0;
};

export const getStripeSavingsSelector = (planTypeId) => (state) => {
  const products = state.configuration.stripeProducts;
  return products[planTypeId.toString()]?.savings || 0;
};

export const { clearConfiguration } = configurationSlice.actions;

export default configurationSlice.reducer;
